import { StreamChat, Channel } from 'stream-chat';
import { AppThunkAction } from 'src/store/reduxTypes';
import {
  MessagesActionTypes,
  MessageChannelEntity,
} from 'src/store/messages/types';
import MessagesClient from 'src/clients/MessagesClient';
import { removeRedundancy } from 'src/utils/ArrayUtils';
import { GetCurrentUser } from 'src/store/users/reducers';

interface ChannelMetaData {
  name?: string;
  members: string[];
  userPoolId: string;
  companyID?: string;
  singleClientId?: string;
  isGroupType?: boolean;
}

const startInitializeClientAction = () => ({
  type: MessagesActionTypes.INTIALIZE_CLIENT_START,
});

const getStreamClientSetAction = (currentUserId?: string) => ({
  type: MessagesActionTypes.INITIALIZE_CLIENT_DONE,
  currentUserId,
});

export const initializeClientAction = (
  client: StreamChat,
): AppThunkAction => async (dispatch, getState) => {
  const {
    user: { id: currentUserId, isClient },
    messages: messagesState,
    users: usersState,
  } = getState();
  if (messagesState.clientInitialized || messagesState.clientInitializing) {
    // do no initialize client if it is already initialized or initializing
    return;
  }
  dispatch(startInitializeClientAction());

  const currentUser = GetCurrentUser({
    isClient,
    currentUserId,
    usersState,
  });
  let getStreamId = '';
  if (currentUser) {
    let token = '';
    try {
      token = await MessagesClient.GetUserToken();
    } catch (error) {
      console.error('failed to get chat user token', error);
    }

    getStreamId = currentUser.getstreamId;
    if (token && getStreamId) {
      try {
        await client.connectUser(
          {
            id: getStreamId,
            name: `${currentUser.fields.given_name} ${currentUser.fields.family_name}`,
            image: currentUser.fields.picture,
            fallbackColor: currentUser.fields.fallbackColor,
          },
          token,
        );
      } catch (ex) {
        console.error('failed to connect user with chat', ex);
      }

      if (getStreamId) {
        try {
          await MessagesClient.SetUserRole(currentUser.id);
        } catch (error) {
          console.error('failed to set user role', error);
        }
      }
    }
  }

  dispatch(getStreamClientSetAction(getStreamId));
};

export const createChannel = (
  client: StreamChat,
  newChannelData: ChannelMetaData,
): AppThunkAction => async dispatch => {
  function done(channel: Channel) {
    return {
      type: MessagesActionTypes.SET_CREATED_CHANNEL,
      channelId: channel.id,
    };
  }

  const channelId =
    Math.random()
      .toString(36)
      .substring(2, 15) +
    Math.random()
      .toString(36)
      .substring(2, 15);

  const conversation = client.channel('messaging', channelId, newChannelData);

  await conversation.watch();

  dispatch(done(conversation));
};

export const setTotalUnreadMessages = (totalUnreadMessagesCount: number) => ({
  type: MessagesActionTypes.SET_UNREAD_MESSAGES_COUNT,
  payload: totalUnreadMessagesCount,
});

export const addDeletedChannel = (channelID: string) => ({
  type: MessagesActionTypes.ADD_DELETED_CHANNEL,
  payload: channelID,
});

export const setNoLoadedChannel = (notLoaded: boolean) => ({
  type: MessagesActionTypes.SET_NO_CHANNEL_LOADED,
  payload: notLoaded,
});

export const setLoadingChannelError = (error: string) => ({
  type: MessagesActionTypes.SET_LOADING_CHANNEL_ERROR,
  payload: error,
});

export const createInternalChannel = (
  client: StreamChat,
  channelItem: MessageChannelEntity,
  portalConfig: any,
  singleClientId?: string,
): AppThunkAction => async (dispatch, getState) => {
  const messagesState = getState().messages;
  if (!messagesState.currentUserId) {
    return;
  }
  const mergedMemberIDs = channelItem.fields.getstreamMembers.concat([
    messagesState.currentUserId,
  ]);

  // const formattedMemberIDs = channelItem.fields.members
  //   .map(memberID => getFullUserIdFromUserId(portalConfig, memberID))
  //   .concat([messagesState.currentUserId]);
  const channelMetaData: ChannelMetaData = {
    members: mergedMemberIDs,
    name: channelItem.fields.name,
    userPoolId: portalConfig.portalHeader,
    companyID: channelItem.fields.companyID,
  };
  if (singleClientId) {
    channelMetaData.singleClientId = singleClientId;
  }
  // add group metadata tag if no clientId or companyId is present
  if (!singleClientId && !channelItem.fields.companyID) {
    channelMetaData.isGroupType = true;
  }
  if (channelItem.fields.members.length > 0) {
    await MessagesClient.SetupChannelMembers(
      removeRedundancy(channelItem.fields.members),
    );
  }
  dispatch(createChannel(client, channelMetaData));
};
